// React
import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AxiosError } from 'axios';
import { I18nextProvider } from 'react-i18next';
import { Outlet, useLocation } from 'react-router-dom';
import { initPreviewFromUrl } from '@/utils/preview';

// GSAP
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollSmoother } from 'gsap/ScrollSmoother';

// Store
import { AppProvider } from '@/store/AppContext';

// Animations
import ScrollToTop from '@/components/reveal-animations/ScrollToTop';

// Sentry init
import { isInSentryExceptionsAllowList } from '@/services/sentryAllowList';

// i18n
import i18n from '@/i18n';

gsap.registerPlugin(ScrollTrigger, ScrollSmoother);

// Sentry init
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  integrations: [Sentry.replayIntegration()],

  beforeSend(event, hint) {
    const error = hint.originalException;
    if (error instanceof AxiosError) {
      if (isInSentryExceptionsAllowList(error)) {
        return null;
      }
      return {
        ...event,
        extra: {
          ...event.extra,
          error: error.response?.data,
        },
      };
    }
    return event;
  },

  enabled: !import.meta.env.DEV,
  environment: import.meta.env.MODE,

  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for tracing.
  tracesSampleRate: 1.0,

  // Capture Replay for 10% of all sessions,
  // plus for 100% of sessions with an error
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});

// Create a query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 0,
      refetchOnWindowFocus: false,
      throwOnError: true,
    },
    mutations: {
      throwOnError: true,
    },
  },
});

/**
 * The layout component that wraps all pages.
 * @returns {JSX.Element} The Layout component
 */
function App() {
  const { pathname } = useLocation();

  useEffect(() => {
    initPreviewFromUrl();
  }, [pathname]);

  useEffect(() => {
    const smoother = ScrollSmoother.create({
      wrapper: '#smooth-wrapper',
      content: '#smooth-content',
      smooth: 1.5,
      effects: true,
    });
    return () => smoother.kill();
  }, []);

  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      ScrollTrigger.refresh();
    });
    return () => cancelAnimationFrame(raf);
  }, [pathname]);

  return (
    <I18nextProvider i18n={i18n}>
      <QueryClientProvider client={queryClient}>
        <AppProvider>
          <ScrollToTop />
          <div id="smooth-wrapper">
            {/* Fixed elements (Header, BackgroundBubble) are portaled here by LayoutHeaderFooter
                so they stay outside #smooth-content and preserve position:fixed behaviour */}
            <div id="fixed-layer" />
            <div id="smooth-content">
              <Outlet />
            </div>
          </div>
        </AppProvider>
        <ReactQueryDevtools initialIsOpen />
      </QueryClientProvider>
    </I18nextProvider>
  );
}

export default App;
